import BtxSwitch from '../BtxSwitch';
import React, { useMemo } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { Form, Field } from 'react-final-form';
import { actions } from '../../../context';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { groupedStatesProvinces, states, canadaProvinces } from '../../Static/StatesProvinces';
import { useState } from 'react';
import FieldTitle from '../FieldTitle';
import CancelModalBumper from './CancelModalBumper';
import EditPracticeFormField from './EditPracticeFormField';

const otherKey = 'Other';
const formColClass = 'px-2 py-1';
const lgColSize = 6;
const mdColSize = 12;

const PracticeEditingForm = ({ canManagePracticeGroups }) => {
    const dispatch = useDispatch();

    const cancel = () =>
        dispatch(actions.toggle.toggleIsEditingPractice(false));
    // conditionaly rendered and stops the data sync, so no diffing needed
    const practice = useSelector((state) => state.practice.practice);
    const groups = useSelector(
        (state) => state.practice.practiceGroups,
        groupEqualityCheck
    );
    const pims = useSelector(
        (state) => state.pims.allPims,
        allPimsEqualityCheck
    );
    const integrationSupportedPims = useMemo(
        () => pims.filter((p) => p.supportsConnectivity),
        [pims]
    );
    const manuallySupportedPims = useMemo(
        () =>
            pims.filter((p) => !p.supportsConnectivity && p.name !== otherKey),
        [pims]
    );
    const notListedPims = useMemo(
        () =>
            pims.filter((p) => !p.supportsConnectivity && p.name === otherKey),
        [pims]
    );

    const [isTestPractice, setIsTestPractice] = useState(
        practice?.isTestPracticeProp
    );
    const [customElementsTouched, setCustomElementsTouched] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);

    const cancelEdit = (form, pristine, override = false) => {
        if (override) {
            if (form) form.reset();
            setCancelModalVisible(false);
            cancel();
        } else {
            if (pristine) cancelEdit(form, pristine, true);
            else setCancelModalVisible(true);
        }
    };

    const submit = async (vals) => {
        let valuesToSubmit = {
            ...vals,
            usingOtherPims: isUsingOther(vals),
            isTestPractice: isTestPractice,
        };
        dispatch(actions.practice.saveEditedPracticeInfo(valuesToSubmit))
            .then(() => {
                dispatch(actions.toggle.triggerRefresh());
                dispatch(actions.toggle.triggerPracticeRefresh());
            })
            .finally(() => cancel());
    };

    const toggleTestPractice = () => {
        setCustomElementsTouched(true);
        setIsTestPractice(!isTestPractice);
    };

    const havePimsWithId = (id) =>
        id && pims.filter((x) => x.id === id).length > 0;

    const isUsingOther = (values) =>
        values &&
        havePimsWithId(values.pimsId) &&
        pims.filter((x) => x.id === values.pimsId)[0].name === otherKey;

    const getCountryFromStateProvince = (stateProvinceValue) => {
        if (!stateProvinceValue) return null;
        
        const usState = states.find(s => s.value === stateProvinceValue);
        if (usState) return 'United States';
        
        const caProvince = canadaProvinces.find(p => p.value === stateProvinceValue);
        if (caProvince) return 'Canada';
        
        return null;
    };

    const formComponent = ({
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
    }) => (
        <form onSubmit={handleSubmit}>
            <Row>
                {/* title == field label element, not a title attribute */}
                <EditPracticeFormField
                    title={'Practice Name'}
                    name={'name'}
                    placeholder={'Name'}
                    required
                />
                <EditPracticeFormField
                    title={'Phone Number'}
                    name={'phone'}
                    placeholder={'Phone'}
                />
                <EditPracticeFormField
                    title={'Street Address'}
                    name={'address'}
                    placeholder={'Address'}
                />
                <EditPracticeFormField
                    title={'City'}
                    name={'city'}
                    placeholder={'City'}
                />
                <Col lg={lgColSize} md={mdColSize} className={formColClass}>
                    <FieldTitle title="State / Province" required/>
                    <Field
                        name="stateProvince"
                        component="select"
                        className="w-100 p-2 rounded border"
                        defaultValue=""
                        required
                        onChange={(event, input) => {
                            const selectedValue = event.target.value;
                            const country = getCountryFromStateProvince(selectedValue);
                            form.change('stateProvince', selectedValue);
                            form.change('country', country);
                        }}>
                        <option value="" disabled>
                            Select a region
                        </option>
                        <option />
                        {groupedStatesProvinces.map((g) => (
                            <optgroup key={g.label} label={g.label}>
                                {g.options.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </Field>
                </Col>
                {/* Hidden field to store the country value */}
                <Field name="country" component="input" type="hidden" />
                <EditPracticeFormField
                    title={'Postal Code'}
                    name={'postalCode'}
                    placeholder={'Code'}
                    required
                />
                <EditPracticeFormField
                    title={'Practice Contact'}
                    name={'primaryContact'}
                    placeholder={'Contact'}
                />
                <EditPracticeFormField
                    title={'Contact Email'}
                    name={'email'}
                    placeholder={'Email'}
                />
                <EditPracticeFormField
                    title={'Practice Website'}
                    name={'website'}
                    placeholder={'Website'}
                />
                <Col lg={lgColSize} md={mdColSize} className={formColClass}>
                    <FieldTitle title="Pims" required />
                    <Field
                        className="w-100 p-2 rounded border"
                        component="select"
                        id="pims"
                        name="pimsId"
                        placeholder="Pims"
                        defaultValue=""
                        required>
                        <option value="" disabled>
                            Pims
                        </option>
                        <option />
                        <optgroup label="Integration Supported">
                            {integrationSupportedPims.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </optgroup>
                        <optgroup label="Manually Supported">
                            {manuallySupportedPims.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </optgroup>
                        <optgroup label="Not Listed">
                            {notListedPims.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name} (Specify)
                                </option>
                            ))}
                        </optgroup>
                    </Field>
                </Col>
                {isUsingOther(values) && (
                    <EditPracticeFormField
                        title={'Other Pims Name'}
                        name={'otherPimsName'}
                        placeholder={'Pims Name'}
                        required
                    />
                )}
                {canManagePracticeGroups && (
                    <Col lg={lgColSize} md={mdColSize} className={formColClass}>
                        <FieldTitle title="Practice Group" />
                        <Field
                            id="practiceGroup"
                            name="groupId"
                            component="select"
                            className="w-100 p-2 rounded border"
                            defaultValue="">
                            <option value="" disabled>
                                Group
                            </option>
                            <option />
                            {groups?.map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.name}
                                </option>
                            ))}
                        </Field>
                    </Col>
                )}
                <Col lg={lgColSize} md={mdColSize} className={formColClass}>
                    <FieldTitle title="Test practice?" />
                    <BtxSwitch
                        value={isTestPractice}
                        onChange={toggleTestPractice}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs="12" className="text-right p-2">
                    <Button
                        className="border"
                        color="danger"
                        disabled={submitting}
                        onClick={() => cancelEdit(form, pristine)}>
                        Cancel
                    </Button>
                    &emsp;
                    <Button
                        className="border"
                        color="primary"
                        type="submit"
                        disabled={
                            (pristine || submitting) && !customElementsTouched
                        }>
                        Save
                    </Button>
                </Col>
            </Row>
            {cancelModalVisible && (
                <CancelModalBumper
                    setCancelModalVisible={setCancelModalVisible}
                    cancelEdit={cancelEdit}
                    form={form}
                    pristine={pristine}
                />
            )}
        </form>
    );

    return (
        <div>
            <Form
                initialValues={{ 
                    ...practice,
                    country: practice?.stateProvince ? getCountryFromStateProvince(practice.stateProvince) : null 
                }}
                onSubmit={submit}
                render={formComponent}></Form>
        </div>
    );
};

const groupEqualityCheck = (newGrpArray, oldGrpArray) => {
    if (oldGrpArray.length !== newGrpArray.length) return false;
    if(oldGrpArray.length === 0 && newGrpArray.length === 0) return true;
    const practiceGroupEqualityCheck = (oldGrp) => {
        const compareGrps = (newGrp) => {
            return shallowEqual(newGrp, oldGrp);
        };
        return newGrpArray.some(compareGrps);
    };

    const comparedGrpList =
        !!oldGrpArray.length && oldGrpArray.every(practiceGroupEqualityCheck);

    const isSameGrpList = !!newGrpArray.length && comparedGrpList;

    return isSameGrpList;
};
const allPimsEqualityCheck = (newPimsList, oldPimsList) => {
    if (oldPimsList.length !== newPimsList.length) return false;
    if(oldPimsList.length === 0 && newPimsList.length === 0) return true;
    const pimsEqualityCheck = (oldPims) => {
        const template = {
            id: '',
            name: '',
            supportsConnectivity: false,
        };
        const oldState = Object.assign(
            {},
            template,
            ...Object.keys(template).map(
                (k) => k in oldPims && { [k]: oldPims[k] }
            )
        );

        const comparePims = (newPims) => {
            const newState = Object.assign(
                {},
                template,
                ...Object.keys(template).map(
                    (k) => k in newPims && { [k]: newPims[k] }
                )
            );
            return shallowEqual(oldState, newState);
        };
        const check = newPimsList.find(comparePims);
        return check !== undefined;
    };
    const comparedPimsList =
        !!oldPimsList.length && oldPimsList.every(pimsEqualityCheck);

    const isSamePimsList = !!newPimsList.length && comparedPimsList;

    return isSamePimsList;
};

export default PracticeEditingForm;
